import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'reducers';

/**
 * Memoized selector that combines all Scheduled Maintenance page data.
 * Prevents unnecessary re-renders by only updating when actual data changes.
 *
 * This selector consolidates data from:
 * - scheduledMaintenanceStore: All maintenance-related state
 * - userStore: User context and account information
 *
 * @returns Complete context object for ScheduledMaintenance component
 */
export const selectScheduledMaintenancePageContext = createSelector(
  [(state: RootState) => state.scheduledMaintenanceStore, (state: RootState) => state.userStore],
  (scheduledMaintenanceStore, userStore) => ({
    // Scheduled Maintenance - Single Item
    scheduledMaintenance: scheduledMaintenanceStore.scheduledMaintenance,
    scheduledMaintenanceLoading: scheduledMaintenanceStore.scheduledMaintenanceLoading,
    scheduledMaintenanceError: scheduledMaintenanceStore.scheduledMaintenanceError,

    // Scheduled Maintenance - List Data
    scheduledMaintenanceList: scheduledMaintenanceStore.scheduledMaintenanceList,
    scheduledMaintenanceListSize: scheduledMaintenanceStore.scheduledMaintenanceListSize,
    scheduledMaintenanceListLoading: scheduledMaintenanceStore.scheduledMaintenanceListLoading,
    scheduledMaintenanceListError: scheduledMaintenanceStore.scheduledMaintenanceListError,

    // Scheduled Maintenance - Create Operation
    createScheduledMaintenanceLoading: scheduledMaintenanceStore.createScheduledMaintenanceLoading,
    createScheduledMaintenanceLoaded: scheduledMaintenanceStore.createScheduledMaintenanceLoaded,

    // Scheduled Maintenance - Update Operation
    updateScheduledMaintenanceLoading: scheduledMaintenanceStore.updateScheduledMaintenanceLoading,
    updateScheduledMaintenanceLoaded: scheduledMaintenanceStore.updateScheduledMaintenanceLoaded,

    // Scheduled Maintenance - Delete Operation
    deleteScheduledMaintenanceLoading: scheduledMaintenanceStore.deleteScheduledMaintenanceLoading,
    deleteScheduledMaintenanceLoaded: scheduledMaintenanceStore.deleteScheduledMaintenanceLoaded,

    // User Store - User Context
    loggedInUser: userStore.loggedInUser,
    assignedAccountNumber: userStore.assignedAccountNumber,
  })
);

/**
 * Type-safe context type for Scheduled Maintenance
 * Provides autocomplete and type checking for all properties
 */
export type ScheduledMaintenanceContext = ReturnType<typeof selectScheduledMaintenancePageContext>;
